/* eslint-disable no-param-reassign */
import SpecProcessor, { type Spec as CommonSpec, type Data } from './specProcessor';
import * as utils from '../../util';
import type { StandardChartLayerSpec } from '../../core/layer';
import { getStaticMethodNames } from './helper';

type Spec = Omit<CommonSpec, 'layers'> & {
  layers: StandardChartLayerSpec[];
};

type DynamicBeeSwarmLayerType = StandardChartLayerSpec & {
  encoding: {
    y2: string;
    x: string;
    y: string[];
  };
  __isEncodingValid?: boolean;
};

type LayerFunc = (data: Data, spec: Spec) => boolean;

function validateEncoding(layer: DynamicBeeSwarmLayerType): boolean {
  const { encoding } = layer;

  if (!encoding.y2) {
    console.error('The "y2" field of the "dynamicBeeSwarm" layer is required');
    return false;
  }

  if (!encoding.y || encoding.y.length === 0) {
    console.error('The "y" field of the "dynamicBeeSwarm" layer is required');
    return false;
  }

  if (!encoding.x) {
    console.error('The "x" field of the "dynamicBeeSwarm" layer is required');
    return false;
  }

  return true;
}

function validateUniqueNames(data: Data, layer: DynamicBeeSwarmLayerType, isValid: boolean) {
  let validating = isValid;
  const { dataIndex = 0, encoding } = layer;
  const rawData = data[dataIndex].values;
  const datesSortList = utils.dataTransform.transform(rawData.slice(), [
    { type: 'deDuplication', field: encoding.y2 },
    { type: 'sort', field: encoding.y2, order: 'ascending' }
  ]);
  const { dates } = utils.dataEncoding.encoding(datesSortList, {
    dates: { field: encoding.y2 }
  });

  dates.forEach(date => {
    const dateData = rawData.filter(item => item[encoding.y2] === date);
    const nameList = dateData.map(item => item[encoding.x]);
    if (nameList.length !== new Set(nameList).size) {
      console.error(
        `The name of the corresponding "${encoding.x}" field in the data of each "${date}" period in the "dynamicBeeSwarm" cannot be repeated`
      );
      validating = false;
      layer.__isEncodingValid = false;
    }
  });
  return validating;
}

export default class StandardChartSpecProcessor extends SpecProcessor {
  static dynamicBeeSwarm(data: Data, spec: Spec): boolean {
    let isValid = true;
    const { layers = [] } = spec;

    if (layers.some(layer => layer.type !== 'dynamicBeeSwarm')) {
      return isValid;
    }

    layers
      .filter((layer): layer is DynamicBeeSwarmLayerType => layer.type === 'dynamicBeeSwarm')
      .forEach(layer => {
        if (!validateEncoding(layer)) {
          isValid = false;
          layer.__isEncodingValid = false;
        } else {
          isValid = validateUniqueNames(data, layer, isValid);
        }
      });

    return isValid;
  }

  static isDataValid(data: Data, spec: Spec): boolean {
    let isValid = true;
    const { layers = [] } = spec;
    const layerNames = getStaticMethodNames(this);
    const supportedLayers: Record<string, LayerFunc> = layerNames.reduce((acc, name) => {
      acc[name] = StandardChartSpecProcessor[
        name as keyof typeof StandardChartSpecProcessor
      ] as LayerFunc;
      return acc;
    }, {});
    layers.forEach(layer => {
      if (supportedLayers[layer.type]) {
        isValid = isValid && supportedLayers[layer.type](data, spec);
      }
    });
    return isValid;
  }

  static preprocessing(data: Data, spec: Spec): boolean {
    return (
      StandardChartSpecProcessor.isDataValid(data, spec) &&
      (StandardChartSpecProcessor.isEncodingValid(data, spec) as boolean)
    );
  }
}
